import { supabase } from "@/integrations/supabase/client";
import { getFullDocumentHtml } from "./documentHtml";
import { generatedFilePath } from '@/components/workflows/docProvenance';

export interface PdfGenerationResult {
  file_url: string;
  file_path: string;
  file_size: number;
  pdfBlob: Blob;
}

const base64ToBlob = (base64: string, type: string): Blob => {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type });
};

export async function generateDocumentPdf(
  documentId: string,
  documentName: string
): Promise<PdfGenerationResult> {
  // 1. Get FULL HTML document for PDF generation (not just body content)
  const html = await getFullDocumentHtml(documentId);

  // 2. Get auth session
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    throw new Error('No active session');
  }

  // 3. Call generate-pdf-server edge function
  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-pdf-server`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ html }),
    }
  );

  if (!response.ok) {
    let errorMessage = 'Failed to generate PDF';
    try {
      const errorData = await response.json();
      errorMessage = errorData.error || errorData.message || errorMessage;
      console.error('❌ PDF generation API error:', errorData);
    } catch (e) {
      const text = await response.text();
      console.error('❌ PDF generation error response:', text);
      errorMessage = text || errorMessage;
    }
    throw new Error(errorMessage);
  }

  const { pdf: pdfBase64, size } = await response.json();

  // 4. Convert base64 to Blob
  const pdfBlob = base64ToBlob(pdfBase64, 'application/pdf');

  // 5. Upload to storage
  const filePath = generatedFilePath(null, documentName, documentId);

  const { error: uploadError } = await supabase.storage
    .from('documents')
    .upload(filePath, pdfBlob, {
      contentType: 'application/pdf',
      upsert: true,
    });

  if (uploadError) throw uploadError;

  // 6. Return file_path (no public URL — buckets are private)
  return {
    file_url: filePath, // Store path as reference; signed URLs generated on access
    file_path: filePath,
    file_size: size,
    pdfBlob,
  };
}
